import { redirect } from '@tanstack/react-router';
import type { Session } from '@supabase/supabase-js';
import { supabase as defaultClient } from '../supabase';
import { extractTenantClaims, type UserType } from './jwt';

export type RequireAuthArgs = {
  pathname: string;
  search?: Record<string, unknown>;
  // Injected for tests; defaults to the singleton supabase client.
  getSession?: () => Promise<{ data: { session: Session | null } }>;
};

// TanStack Router beforeLoad guard. Reads the session directly from
// supabase (NOT from React context — beforeLoad runs outside the tree).
// On unauthenticated navigation we throw a redirect to /login carrying
// the intended destination as ?redirect=<path>, which login.tsx will
// honor on success.
export async function requireAuth(args: RequireAuthArgs): Promise<void> {
  const getSession = args.getSession ?? (() => defaultClient.auth.getSession());
  const { data } = await getSession();
  if (data.session) return;

  const target = buildRedirectTarget(args.pathname, args.search);
  throw redirect({
    to: '/login',
    search: { redirect: target },
  });
}

export function buildRedirectTarget(pathname: string, search?: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  if (search) {
    for (const [key, value] of Object.entries(search)) {
      if (value === undefined || value === null) continue;
      usp.set(key, String(value));
    }
  }
  const qs = usp.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export type RequireUserTypeArgs = {
  allowed: readonly UserType[];
  // Where to bounce non-matching callers. Defaults to '/' (the role-aware
  // landing route — dispatchers will re-redirect to /dispatch, others land on
  // the dashboard).
  fallback?: string;
  // Injected for tests; defaults to the singleton supabase client.
  getSession?: () => Promise<{ data: { session: Session | null } }>;
};

// Role gate for routes whose data shape only makes sense for specific user
// types — e.g. /dispatch's tenant-wide workload planner. `platform_admin`
// is ALWAYS allowed (LYN-staff override mirrors the Sidebar pattern). The
// sidebar already hides these from the wrong roles; this guard is the
// defense-in-depth layer so a user who types the URL manually doesn't
// reach the page. RLS remains the safety net under the page's queries.
export async function requireUserType(args: RequireUserTypeArgs): Promise<void> {
  const getSession = args.getSession ?? (() => defaultClient.auth.getSession());
  const { data } = await getSession();
  const token = data.session?.access_token ?? null;
  const { userType } = extractTenantClaims(token);

  if (userType === 'platform_admin') return;
  if (userType && args.allowed.includes(userType)) return;

  throw redirect({ to: args.fallback ?? '/' });
}

// Validate a ?redirect= search param coming back to /login. Reject
// anything that smells like an open redirect: protocol-relative URLs,
// absolute URLs, or paths that don't start with a single forward slash.
// Falling back to '/' is intentional — preserving destination is a UX
// nicety, not a security boundary.
export function safeRedirectPath(value: unknown, fallback: string = '/'): string {
  if (typeof value !== 'string') return fallback;
  if (!value.startsWith('/')) return fallback;
  if (value.startsWith('//')) return fallback;
  if (value.startsWith('/\\')) return fallback;
  return value;
}
